import React, { useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';

const TimelineVisualization = ({ activeView, setActiveView, onMilestoneSelect }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [zoomLevel, setZoomLevel] = useState(100);

  // Mock timeline data
  const milestones = [
    {
      id: 1,
      title: 'Annual Safety Audit',
      regulation: 'OSHA 1910',
      deadline: '2025-01-15',
      status: 'pending',
      location: 'Plant A',
      progress: 65
    },
    {
      id: 2,
      title: 'Environmental Compliance Report',
      regulation: 'EPA Clean Air Act',
      deadline: '2025-01-20',
      status: 'completed',
      location: 'All Sites',
      progress: 100
    },
    {
      id: 3,
      title: 'Fire Safety Certification Renewal',
      regulation: 'NFPA 101',
      deadline: '2025-02-01',
      status: 'pending',
      location: 'Plant B',
      progress: 40
    },
    {
      id: 4,
      title: 'Hazardous Material Storage Inspection',
      regulation: 'RCRA',
      deadline: '2024-12-28',
      status: 'overdue',
      location: 'Warehouse C',
      progress: 75
    },
    {
      id: 5,
      title: 'Emergency Response Plan Update',
      regulation: 'OSHA 1910.38',
      deadline: '2025-03-15',
      status: 'pending',
      location: 'All Sites',
      progress: 20
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500';
      case 'pending':
        return 'bg-yellow-500';
      case 'overdue':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusBorder = (status) => {
    switch (status) {
      case 'completed':
        return 'border-green-500';
      case 'pending':
        return 'border-yellow-500';
      case 'overdue':
        return 'border-red-500';
      default:
        return 'border-gray-500';
    }
  };

  const handlePreviousMonth = () => {
    const newDate = new Date(currentMonth);
    newDate?.setMonth(newDate?.getMonth() - 1);
    setCurrentMonth(newDate);
  };

  const handleNextMonth = () => {
    const newDate = new Date(currentMonth);
    newDate?.setMonth(newDate?.getMonth() + 1);
    setCurrentMonth(newDate);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Timeline Controls */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setActiveView('monthly')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeView === 'monthly' ?'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setActiveView('quarterly')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeView === 'quarterly' ?'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Quarterly
            </button>
            <button
              onClick={() => setActiveView('annual')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeView === 'annual' ?'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Annual
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handlePreviousMonth}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg">
              <Calendar className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-900">
                {currentMonth?.toLocaleString('default', { month: 'long', year: 'numeric' })}
              </span>
            </div>
            <button
              onClick={handleNextMonth}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setZoomLevel(Math.max(50, zoomLevel - 10))}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ZoomOut className="w-5 h-5 text-gray-600" />
            </button>
            <span className="text-sm text-gray-600 min-w-[60px] text-center">
              {zoomLevel}%
            </span>
            <button
              onClick={() => setZoomLevel(Math.min(200, zoomLevel + 10))}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ZoomIn className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
      </div>
      {/* Timeline Gantt Chart */}
      <div className="p-6">
        <div className="space-y-4">
          {milestones?.map((milestone) => (
            <div
              key={milestone?.id}
              className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => onMilestoneSelect(milestone)}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-3 h-3 rounded-full ${getStatusColor(milestone?.status)}`} />
                    <h3 className="font-medium text-gray-900">{milestone?.title}</h3>
                    <span className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded">
                      {milestone?.regulation}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600 ml-6">
                    <span>Location: {milestone?.location}</span>
                    <span>Deadline: {new Date(milestone.deadline)?.toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900 mb-1">
                    {milestone?.progress}%
                  </div>
                  <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${getStatusColor(milestone?.status)}`}
                      style={{ width: `${milestone?.progress}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Timeline Bar */}
              <div className="mt-4 ml-6">
                <div className="relative h-8 bg-gray-100 rounded-lg overflow-hidden">
                  <div
                    className={`absolute top-0 bottom-0 left-0 ${getStatusColor(
                      milestone?.status
                    )} opacity-30`}
                    style={{ width: `${milestone?.progress}%` }}
                  />
                  <div
                    className={`absolute top-1/2 -translate-y-1/2 h-4 rounded border-2 ${getStatusBorder(
                      milestone?.status
                    )} bg-white`}
                    style={{
                      left: '10%',
                      width: `${milestone?.progress}%`
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Legend */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center gap-6 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span className="text-sm text-gray-700">Completed</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <span className="text-sm text-gray-700">Pending</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <span className="text-sm text-gray-700">Overdue</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelineVisualization;